/**
 * What can be bought, and what each purchase entitles — RX-SUB-02/03/04/05.
 *
 * One list, shared by the phone, the API and the three places money arrives
 * from. A purchase turns up as an opaque identifier — Apple sends a product
 * id, Google sends a subscription id and a base plan, Razorpay sends a plan
 * id — and every one of them has to resolve to the same two facts: which
 * tier, and for how long. Keeping that mapping in one file is what stops a
 * renewal on one store granting something different from the same renewal on
 * another.
 *
 * ── The identifiers are permanent ────────────────────────────────────────────
 * Neither store lets a product identifier be renamed or reused once created,
 * and a typo is a product you live with or abandon. They are written out here
 * rather than composed from parts, so that what goes in the console is copied
 * from the same string the code matches on.
 *
 * ── Prices are not here ──────────────────────────────────────────────────────
 * The stores hold the price, in every currency, and they are the only source
 * that knows what a person is actually charged after local tax and Apple's or
 * Google's own rounding. The figures in `docs/06-features.md` are the
 * intention; what a customer pays is whatever the store says. Nothing in the
 * product should quote a price it has not been told by a store.
 */
import type { Tier } from './entitlements.js';

export type BillingPeriod = 'monthly' | 'annual';

export interface Product {
  /** Stable internal name, used in logs and the admin console. */
  readonly key: string;
  readonly tier: Extract<Tier, 'plus' | 'family'>;
  readonly period: BillingPeriod;
  /** App Store Connect product id, inside the DrBell subscription group. */
  readonly apple: string;
  /** Play subscription id, and the base plan within it. */
  readonly google: { readonly subscription: string; readonly basePlan: string };
  /** Razorpay plan id, for the web checkout. Filled in when created. */
  readonly razorpay: string | null;
}

/**
 * Two tiers, two periods.
 *
 * No monthly/annual price experiments and no legacy tiers: every extra
 * product is one more thing that can be bought now and has to be honoured
 * for years, because a subscriber on a withdrawn product keeps it.
 */
export const PRODUCTS: readonly Product[] = [
  {
    key: 'plus-monthly',
    tier: 'plus',
    period: 'monthly',
    apple: 'app.drbell.plus.monthly',
    google: { subscription: 'plus', basePlan: 'plus-monthly' },
    razorpay: null,
  },
  {
    key: 'plus-annual',
    tier: 'plus',
    period: 'annual',
    apple: 'app.drbell.plus.annual',
    google: { subscription: 'plus', basePlan: 'plus-annual' },
    razorpay: null,
  },
  {
    key: 'family-monthly',
    tier: 'family',
    period: 'monthly',
    apple: 'app.drbell.family.monthly',
    google: { subscription: 'family', basePlan: 'family-monthly' },
    razorpay: null,
  },
  {
    key: 'family-annual',
    tier: 'family',
    period: 'annual',
    apple: 'app.drbell.family.annual',
    google: { subscription: 'family', basePlan: 'family-annual' },
    razorpay: null,
  },
];

/**
 * The product an Apple purchase refers to.
 *
 * Apple's notifications and receipts carry the product id and nothing else
 * that identifies the plan, so this is the only way back to a tier.
 */
export function productForApple(productId: string): Product | null {
  return PRODUCTS.find((p) => p.apple === productId) ?? null;
}

/**
 * The product a Play purchase refers to.
 *
 * Google separates the subscription from the base plan, and the tier lives on
 * the subscription while the period lives on the base plan — so both are
 * needed. A base plan the server has never heard of resolves to null rather
 * than guessing at the subscription alone, because guessing would grant an
 * annual entitlement for a monthly payment.
 */
export function productForGoogle(subscriptionId: string, basePlanId: string): Product | null {
  return (
    PRODUCTS.find(
      (p) => p.google.subscription === subscriptionId && p.google.basePlan === basePlanId,
    ) ?? null
  );
}

/** The product a Razorpay subscription refers to. */
export function productForRazorpay(planId: string): Product | null {
  return PRODUCTS.find((p) => p.razorpay !== null && p.razorpay === planId) ?? null;
}

/** Every Apple product id, for the phone to ask the store about. */
export const APPLE_PRODUCT_IDS: readonly string[] = PRODUCTS.map((p) => p.apple);

/** Every Play subscription id, deduplicated — Play is asked per subscription. */
export const GOOGLE_SUBSCRIPTION_IDS: readonly string[] = [
  ...new Set(PRODUCTS.map((p) => p.google.subscription)),
];
